import {
  ReadonlyEventWatchable,
  ReadonlyWatchable,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import {
  createDefaultWebhookProviderConfigDraft,
  webhookTriggerSettingsToConfigDraft,
} from '@cotera/client/app/v1/webhook-providers/webhook-config-draft';
import type {
  WebhookProviderConfigDraft,
  WebhookTriggerSettings,
  WebhookTriggerSettingsLoad,
} from '@cotera/client/app/v1/webhook-providers/types';
import type { WatchablesFactory } from '../../watchables/watchables-factory';
import type { WorkflowGraphApiData } from '../workflow-graph.types';
import { readWebhookConfigDraftFromGraph } from './webhook-trigger-graph';

type WorkflowWebhookTriggerEditModelProps = {
  triggerId: string;
  isDraftOnly: boolean;
  graphWatchable: ReadonlyWatchable<WorkflowGraphApiData | null>;
  client: Pick<TenantedClient, 'resource'>;
  watchablesFactory: Pick<
    WatchablesFactory,
    'makeWebhookTriggerSettingsWatchable'
  >;
};

/**
 * Edit-time state for a workflow webhook trigger: server settings or draft graph
 * as baseline, with local overrides merged via {@link Watchable.from}.
 */
export class WorkflowWebhookTriggerEditModel {
  readonly triggerId: string;
  readonly isDraftOnly: boolean;
  readonly baselineWatchable: ReadonlyWatchable<WebhookProviderConfigDraft | null>;
  readonly draftWatchable: ReadonlyWatchable<WebhookProviderConfigDraft>;
  readonly isReadyWatchable: ReadonlyWatchable<boolean>;
  readonly settingsWatchable: ReadonlyWatchable<WebhookTriggerSettings | null>;
  /**
   * Why the settings could not be read, or null. Separate from "not ready" so
   * the editor can say what went wrong instead of waiting forever.
   */
  readonly loadErrorWatchable: ReadonlyWatchable<string | null>;

  private readonly _overridesWatchable: Watchable<
    Partial<WebhookProviderConfigDraft>
  >;
  private readonly _settingsEventWatchable: ReadonlyEventWatchable<WebhookTriggerSettingsLoad> | null;
  private readonly _dispose: () => void;

  constructor(props: WorkflowWebhookTriggerEditModelProps) {
    this.triggerId = props.triggerId;
    this.isDraftOnly = props.isDraftOnly;
    this._overridesWatchable = Watchable.fromValue<
      Partial<WebhookProviderConfigDraft>
    >({});

    if (props.isDraftOnly) {
      this._settingsEventWatchable = null;
      this.settingsWatchable =
        Watchable.fromValue<WebhookTriggerSettings | null>(null);
      this.loadErrorWatchable = Watchable.fromValue<string | null>(null);
      this.baselineWatchable = Watchable.from((get) => {
        const graph = get(props.graphWatchable);
        if (graph === null) {
          return createDefaultWebhookProviderConfigDraft();
        }
        return readWebhookConfigDraftFromGraph(graph, props.triggerId);
      });
      this.isReadyWatchable = Watchable.fromValue(true);
      this._dispose = () => {};
    } else {
      const settingsEventWatchable =
        props.watchablesFactory.makeWebhookTriggerSettingsWatchable({
          triggerId: props.triggerId,
          client: props.client,
        });
      this._settingsEventWatchable = settingsEventWatchable;
      this.settingsWatchable = Watchable.from((get) => {
        const load = get(settingsEventWatchable);
        return load.status === 'loaded' ? load.settings : null;
      });
      this.loadErrorWatchable = Watchable.from((get) => {
        const load = get(settingsEventWatchable);
        return load.status === 'error' ? load.message : null;
      });
      this.baselineWatchable = Watchable.from((get) => {
        const load = get(settingsEventWatchable);
        if (load.status !== 'loaded') {
          return null;
        }
        return webhookTriggerSettingsToConfigDraft(load.settings);
      });
      // A failed load is "ready" in the sense the editor cares about: it has
      // stopped waiting. What it renders then is the error, not the form.
      this.isReadyWatchable = Watchable.from(
        (get) => get(settingsEventWatchable).status !== 'loading'
      );
      this._dispose = () => {
        settingsEventWatchable.unsubscribe?.();
      };
    }

    this.draftWatchable = Watchable.from((get) => {
      const baseline = get(this.baselineWatchable);
      const base = baseline ?? createDefaultWebhookProviderConfigDraft();
      return { ...base, ...get(this._overridesWatchable) };
    });
  }

  patchDraft(patch: Partial<WebhookProviderConfigDraft>): void {
    this._overridesWatchable.set({
      ...this._overridesWatchable.snapshot(),
      ...patch,
    });
  }

  clearOverrides(): void {
    this._overridesWatchable.set({});
  }

  async refreshSettings(): Promise<void> {
    await this._settingsEventWatchable?.refresh?.();
  }

  dispose(): void {
    this._dispose();
  }
}
